import { useState, type JSX } from 'react';
import { Link, useParams } from 'react-router';
import {
  Badge,
  Button,
  Card,
  ChevronLeftIcon,
  PlayIcon,
  SaveIcon,
  Tag
} from '@viniciomendesr/rayrovisk-design-system';
import { useCreatePin, usePrompt } from '../api/queries';
import { AccessStateScreen, accessStateOf } from '../access/AccessState';
import { PromptEditor } from '../components/PromptEditor';
import { categoryLabel } from './categories';

/**
 * Screen 05. The adjustments are the interaction that matters: each one appends
 * a line to the prompt, and what the copy button puts on the clipboard is what
 * is on screen — including the adjustments she just picked.
 */
export function PromptScreen(): JSX.Element {
  const { slug = '' } = useParams();
  const query = usePrompt(slug);
  const createPin = useCreatePin();
  const [picked, setPicked] = useState<string[]>([]);
  const [pinned, setPinned] = useState(false);

  const state = accessStateOf(query.error);
  if (state) {
    return <AccessStateScreen state={state} />;
  }

  if (query.isLoading) {
    return (
      <p style={{ font: '400 13px/1.5 var(--rvk-font-functional)', color: 'var(--rvk-text-tertiary)' }}>
        Abrindo o prompt…
      </p>
    );
  }

  const prompt = query.data?.prompt;
  if (!prompt) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 560 }}>
        <h1 style={{ margin: 0, font: '400 26px/1.15 var(--rvk-font-expressive)' }}>
          Este prompt não existe mais.
        </h1>
        <Link to="/biblioteca">
          <Button variant="secondary" size="sm">
            Voltar para a biblioteca
          </Button>
        </Link>
      </div>
    );
  }

  const text = withAdjustments(prompt.prompt, picked);

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 22, maxWidth: 1120 }}>
      <Link to="/biblioteca">
        <Button variant="ghost" size="sm" iconStart={<ChevronLeftIcon size={14} />}>
          Biblioteca · {categoryLabel(prompt.category)}
        </Button>
      </Link>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {/* Same shape the library rows use for the same fact. */}
          <Badge tone="neutral" variant="outline" size="sm">
            {categoryLabel(prompt.category)}
          </Badge>
        </div>
        <h1
          style={{
            margin: 0,
            font: '400 34px/1.1 var(--rvk-font-expressive)',
            letterSpacing: '-.01em'
          }}
        >
          {prompt.title}
        </h1>
      </div>

      <div className="rvk-split">
        <div className="rvk-split-main" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Card layer="functional" padding="lg">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <Eyebrow>Quando usar</Eyebrow>
              <p style={{ margin: 0, font: '400 15px/1.6 var(--rvk-font-functional)', textWrap: 'pretty' }}>
                {prompt.whenToUse}
              </p>
            </div>
          </Card>

          <Card layer="functional" padding="lg">
            <PromptEditor
              identity={`library:${slug}`}
              template={text}
              surface="library"
              sourceRef={slug}
              actionBeforeCopy={
                <Button
                  variant="secondary"
                  size="sm"
                  loading={createPin.isPending}
                  iconStart={<SaveIcon size={14} />}
                  onClick={() => {
                    // Idempotent by source_ref on the server, so a repeat click
                    // cannot duplicate — the button just says it is already done.
                    void createPin
                      .mutateAsync({ kind: 'library', sourceRef: slug })
                      .then(() => setPinned(true))
                      .catch(() => undefined);
                  }}
                >
                  {pinned ? 'Fixado' : 'Fixar'}
                </Button>
              }
            />
            {createPin.isError && (
              <p style={{ margin: 0, font: '400 13px/1.5 var(--rvk-font-functional)' }}>
                {(createPin.error as Error).message}
              </p>
            )}
          </Card>

          <Card layer="functional" padding="lg">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Eyebrow>O que preencher</Eyebrow>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {prompt.whatToFill.map((item, index) => (
                  <div
                    key={item.marker}
                    className="rvk-marker-row"
                    style={{
                      padding: '9px 0',
                      borderBottom:
                        index === prompt.whatToFill.length - 1
                          ? undefined
                          : '1px solid var(--rvk-border-subtle)'
                    }}
                  >
                    <span style={{ font: '500 12px/1.5 var(--rvk-font-mono)' }}>[{item.marker}]</span>
                    <span
                      style={{
                        font: '400 13.5px/1.5 var(--rvk-font-functional)',
                        color: 'var(--rvk-text-secondary)'
                      }}
                    >
                      {item.explanation}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          <Card layer="functional" padding="lg">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Eyebrow>Exemplo de saída</Eyebrow>
                <Badge tone="neutral" variant="outline" size="sm">
                  gerado com este prompt, sem edição
                </Badge>
              </div>
              <p
                style={{
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  font: '400 13.5px/1.9 var(--rvk-font-functional)',
                  background: 'var(--rvk-surface-2)',
                  border: '1px solid var(--rvk-border-subtle)',
                  borderRadius: 'var(--rvk-radius-md)',
                  padding: 'clamp(14px, 3vw, 18px) clamp(14px, 3vw, 20px)'
                }}
              >
                {prompt.outputExample}
              </p>
            </div>
          </Card>
        </div>

        <div className="rvk-split-aside" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card
            layer="functional"
            padding="md"
            title="Ajustes"
            subtitle="Cada ajuste adiciona uma linha no prompt."
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, paddingTop: 4 }}>
              {prompt.adjustments.map((adjustment) => (
                <Tag
                  key={adjustment}
                  selectable
                  selected={picked.includes(adjustment)}
                  onSelectedChange={(selected) =>
                    setPicked((current) =>
                      selected
                        ? [...current, adjustment]
                        : current.filter((item) => item !== adjustment)
                    )
                  }
                >
                  {adjustment}
                </Tag>
              ))}
            </div>
          </Card>

          <Card layer="expressive" padding="md">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Eyebrow>Com o Gerador de Roteiros Inteligentes</Eyebrow>
              <p style={{ margin: 0, font: '400 17px/1.35 var(--rvk-font-expressive)', textWrap: 'pretty' }}>
                Este prompt já sairia com a sua voz preenchida.
              </p>
              {/* The upgrade shown on one marker instead of argued: what she
                  fills by hand here, the generator fills sozinho. */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 5,
                  padding: '10px 12px',
                  background: 'var(--rvk-surface-1)',
                  border: '1px solid var(--rvk-border-subtle)',
                  borderRadius: 'var(--rvk-radius-md)'
                }}
              >
                <span
                  style={{
                    font: '500 12px/1.5 var(--rvk-font-mono)',
                    color: 'var(--rvk-text-tertiary)',
                    textDecoration: 'line-through'
                  }}
                >
                  [SEU NICHO]
                </span>
                <span style={{ font: '500 12px/1.5 var(--rvk-font-mono)' }}>
                  confeitaria artesanal sob encomenda
                </span>
              </div>
              <span
                style={{
                  font: '400 12.5px/1.5 var(--rvk-font-functional)',
                  color: 'var(--rvk-text-secondary)'
                }}
              >
                Cada colchete chega preenchido com o seu nicho, a sua oferta e as palavras que você
                evita. Você só descreve a tarefa.
              </span>
              <Link to="/gerador">
                <Button variant="primary" size="sm" fullWidth iconStart={<PlayIcon size={14} />}>
                  Ver como funciona
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}

function Eyebrow({ children }: { children: string }): JSX.Element {
  return (
    <span
      style={{
        font: '600 10px/1 var(--rvk-font-functional)',
        letterSpacing: '.13em',
        textTransform: 'uppercase',
        color: 'var(--rvk-text-tertiary)'
      }}
    >
      {children}
    </span>
  );
}

/**
 * Appended, never woven in. The catalogue entry is a written artefact and
 * rewriting its body to fold an adjustment inline would change text a human
 * reviewed.
 */
function withAdjustments(prompt: string, picked: string[]): string {
  if (picked.length === 0) {
    return prompt;
  }
  return [prompt, '', ...picked.map((item) => `> Ajuste: ${item}`)].join('\n');
}
